import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { RefinementSection } from "@/components/studio/refinement-section";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { ContentRecipeService } from "@/lib/api/recipe.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  Category,
  Coach,
  RecipeDietTag,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import { RECIPE_DIET_TAGS, RECIPE_DIET_TAG_LABELS } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { validateThumbnailFile } from "@/lib/thumbnail-validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { RecipeVariantFields, recipeFormError } from "./recipe-variant-fields";

const recipeConfig = CONTENT_TYPE_CONFIG.recipe;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface LanguageVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  aboutLongDescription: string;
  localizedIngredients: string[];
  localizedSteps: string[];
  userTips: string[];
  thumbnailUrl: string | null;
  prepTimeMinutes: number | null;
  cookTimeMinutes: number | null;
}

const emptyVariant = (
  languageCode: string,
  isDefault: boolean,
): LanguageVariantDraft => ({
  languageCode,
  isDefault,
  localizedTitle: "",
  localizedDescription: "",
  aboutLongDescription: "",
  localizedIngredients: [],
  localizedSteps: [],
  userTips: [],
  thumbnailUrl: null,
  prepTimeMinutes: null,
  cookTimeMinutes: null,
});

export default function RecipeCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const guard = (forSubmit: boolean, run: () => void) => {
    const error = recipeFormError(categoryId, variants, forSubmit);
    if (error) toast({ title: error, variant: "destructive" });
    else run();
  };
  const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [categoryId, setCategoryId] = useState("");
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [internalNotes, setInternalNotes] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [dietTags, setDietTags] = useState<RecipeDietTag[]>([]);
  const [servings, setServings] = useState<number | null>(null);
  const [calories, setCalories] = useState<number | null>(null);
  const [variants, setVariants] = useState<LanguageVariantDraft[]>([
    emptyVariant("en", true),
  ]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId]);

  const createMutation = useMutation({
    mutationFn: async (submitForApproval: boolean) => {
      const defaultVariant = variants.find((v) => v.isDefault) ?? variants[0];
      const payload = {
        recipe: {
          categoryId,
          tagOptionIds: selectedTagOptionIds,
          primaryLanguage: defaultVariant.languageCode,
          internalNotes: internalNotes || null,
          authorCoachId,
          dietTags,
          servings,
          calories,
        },
        variants: variants.map((v) => ({
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || null,
          aboutLongDescription: v.aboutLongDescription || null,
          localizedIngredients: v.localizedIngredients,
          localizedSteps: v.localizedSteps,
          userTips: v.userTips,
          thumbnailUrl: v.thumbnailUrl,
          prepTimeMinutes: v.prepTimeMinutes,
          cookTimeMinutes: v.cookTimeMinutes,
          translationStatus: v.isDefault ? "completed" : "pending",
        })),
      };

      const data = await ContentRecipeService.create(payload);

      if (submitForApproval) {
        try {
          await ContentRecipeService.submitForReview(data.id);
        } catch (submitErr: any) {
          const msg = submitErr?.message || "Submission failed";
          throw new Error(`Draft saved but could not submit: ${msg}`);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-recipes"] });
      toast({ title: "Recipe created" });
      setLocation("/studio/recipes");
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/content-recipes"] });
      toast({
        title: err.message || "Failed to create recipe",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnailUrl",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = await validateThumbnailFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid thumbnail",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const result = await uploadFile(file);
    if (result) {
      const updated = [...variants];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: result.objectPath,
      };
      setVariants(updated);
    }
  };

  const updateVariant = (field: keyof LanguageVariantDraft, value: any) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([...variants, emptyVariant(langCode, false)]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (id: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  return (
    <BuilderShell
      contentTypeConfig={recipeConfig}
      mode="create"
      onSave={() => guard(false, () => createMutation.mutate(false))}
      onSubmit={() => guard(true, () => createMutation.mutate(true))}
      isSaving={createMutation.isPending}
      status="draft"
    >
      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
      />

      <BuilderSection
        stepNumber={2}
        title="Author"
        description="Select the coach attributed as the recipe's author"
        defaultOpen={true}
      >
        <div className="space-y-2">
          <Label>Author Coach</Label>
          {isCoachOnly ? (
            <Input
              value={user?.name ?? ""}
              disabled
              data-testid="select-author-coach"
            />
          ) : (
            <Select
              value={authorCoachId || "none"}
              onValueChange={(val) =>
                setAuthorCoachId(val === "none" ? null : val)
              }
            >
              <SelectTrigger data-testid="select-author-coach">
                <SelectValue placeholder="Select author" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No author</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </BuilderSection>

      <BuilderSection
        stepNumber={3}
        title="Nutrition & Diet"
        description="Diet tags, servings, and calorie info — shared across all language variants"
        defaultOpen={true}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Diet Tags</Label>
            <div className="flex flex-wrap gap-2" data-testid="diet-tags-list">
              {RECIPE_DIET_TAGS.map((tag) => {
                const selected = dietTags.includes(tag);
                return (
                  <Badge
                    key={tag}
                    variant={selected ? "default" : "outline"}
                    className="cursor-pointer hover-elevate active-elevate-2"
                    onClick={() => {
                      setDietTags(
                        selected
                          ? dietTags.filter((t) => t !== tag)
                          : [...dietTags, tag],
                      );
                    }}
                    data-testid={`diet-tag-${tag}`}
                  >
                    {RECIPE_DIET_TAG_LABELS[tag]}
                  </Badge>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Servings</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={servings ?? ""}
                onChange={(e) =>
                  setServings(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                placeholder="e.g. 4"
                data-testid="input-servings"
              />
            </div>
            <div className="space-y-2">
              <Label>Calories (per serving)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={calories ?? ""}
                onChange={(e) =>
                  setCalories(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                placeholder="e.g. 320"
                data-testid="input-calories"
              />
            </div>
          </div>
        </div>
      </BuilderSection>

      <LanguageVariantsSection
        stepNumber={4}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
      >
        {activeVariant && (
          <RecipeVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            onFileUpload={handleFileUpload}
            isUploading={isUploading}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={5}
        internalNotes={internalNotes}
        onInternalNotesChange={setInternalNotes}
      />
    </BuilderShell>
  );
}
